import React, { memo, type ReactElement, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  FooterActionBar,
  Icon,
  SidePanel,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  ALLOWED_ACTIONS,
  LIST_ACTIONS,
  VIOLATIONS_DETAILS_TABS,
} from '../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../shared/hooks';
import { detailsActions } from '../../../data-store/detailsSlice';
import {
  getHeaderDescription,
  getHeaderIcon,
  getHeaderTitle,
} from '../../../utils';

import ViolationsTabDetailsConflictSP from './ViolationsTabDetailsConflictSP';
import ViolationsTabDetailsMitigateSP from './ViolationsTabDetailsMitigateSP';

import './ViolationsTabDetailsSidePanel.css';

interface ViolationsTabDetailsSidePanelProps {
  activeTab: string;
}

/**
 * ViolationsTabDetailsSidePanel
 */
function ViolationsTabDetailsSidePanel({
  activeTab,
}: ViolationsTabDetailsSidePanelProps): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  const {
    sidepanel: { isSidePanelVisible },
    violationsTab: {
      conflicts: { selectedRowData: conflictsRowData },
      mitigate: { selectedRowData: mitigateRowData },
      actionManager,
    },
  } = useTypedSelector((state) => state.aag.details);

  const {
    violationsTab: {
      controls: { tabInAction },
    },
  } = useTypedSelector((state) => state.aag);

  // Constants
  const actionsList = useMemo(() => {
    if (!actionManager) {
      return [];
    }

    const { allowedActions } = actionManager;

    if (!allowedActions) {
      return [];
    }

    const tempList: string[] = [];

    allowedActions.forEach((action) => {
      if (action in ALLOWED_ACTIONS) {
        tempList.push(
          ...ALLOWED_ACTIONS[action as keyof typeof ALLOWED_ACTIONS]
        );
      }
    });

    return tempList;
  }, [actionManager]);
  const getOptionDisabledStatus = useCallback(
    (value: string) => {
      if (actionManager?.isReadOnlyActions) {
        return !(
          value === LIST_ACTIONS.VIEWDETAILS.value ||
          value === LIST_ACTIONS.GENERATEREPORT.value
        );
      }

      return !actionsList.includes(value);
    },
    [actionManager?.isReadOnlyActions, actionsList]
  );

  const isConflictsTab = Boolean(
    conflictsRowData && activeTab === VIOLATIONS_DETAILS_TABS.TAB_CONFLICTS
  );
  const isMitigatingTab = Boolean(
    mitigateRowData && activeTab === VIOLATIONS_DETAILS_TABS.TAB_MITIGATING
  );

  const handleSidePanelClose = useCallback(
    () => dispatch(detailsActions.setSidePanelClose()),
    [dispatch]
  );

  const header = useMemo(() => {
    if (isConflictsTab) {
      return {
        title: getHeaderTitle(
          conflictsRowData as {
            name?: string;
            entitlementDisplayName?: string;
          },
          conflictsRowData?.tracker.level
        ),
        description: l(getHeaderDescription(conflictsRowData?.tracker.level)),
        icon: getHeaderIcon(conflictsRowData?.tracker.level),
      };
    }

    if (isMitigatingTab) {
      return {
        title: mitigateRowData?.name,
        description: l('Mitigating Control'),
        icon: 'SodMitigate',
      };
    }

    return {};
  }, [l, isConflictsTab, isMitigatingTab, conflictsRowData, mitigateRowData]);

  const getLayout = useCallback(() => {
    if (isConflictsTab) {
      return <ViolationsTabDetailsConflictSP />;
    }

    if (isMitigatingTab) {
      return <ViolationsTabDetailsMitigateSP />;
    }

    return undefined;
  }, [isConflictsTab, isMitigatingTab]);

  const getFooter = useCallback(() => {
    if (isMitigatingTab && tabInAction !== null) {
      return (
        <FooterActionBar className='Sod-ViolationsTabDetailsSidePanel-footerActionBar'>
          <Button
            kind='outlined'
            size='medium'
            onClick={() => {
              dispatch(
                detailsActions.setViolationTabdetailsActionManager({
                  ...actionManager,
                  isOpen: true,
                  sodKey: actionManager?.sodKey ?? '',
                  action: LIST_ACTIONS.CHNAGEMCENDDATE.value,
                  mcKeys: mitigateRowData ? [mitigateRowData.id] : undefined,
                })
              );

              handleSidePanelClose();
            }}
            isDisabled={getOptionDisabledStatus(
              LIST_ACTIONS.CHNAGEMCENDDATE.value
            )}
            leftIcon={<Icon kind='ExtendEndDate' />}>
            {l('Change End Date')}
          </Button>
          <Button
            kind='outlined'
            size='medium'
            onClick={() => {
              dispatch(
                detailsActions.setViolationTabdetailsActionManager({
                  ...actionManager,
                  isOpen: true,
                  sodKey: actionManager?.sodKey ?? '',
                  action: LIST_ACTIONS.REMOVEMC.value,
                  mcKeys: mitigateRowData ? [mitigateRowData.id] : undefined,
                })
              );

              handleSidePanelClose();
            }}
            isDisabled={getOptionDisabledStatus(LIST_ACTIONS.REMOVEMC.value)}
            leftIcon={<Icon kind='Delete' />}>
            {l('Remove')}
          </Button>
        </FooterActionBar>
      );
    }

    return undefined;
  }, [
    isMitigatingTab,
    tabInAction,
    getOptionDisabledStatus,
    l,
    dispatch,
    actionManager,
    mitigateRowData,
    handleSidePanelClose,
  ]);

  return (
    <SidePanel
      isOpen={isSidePanelVisible}
      onClose={handleSidePanelClose}
      appIconSize='medium'
      header={header}
      layout={getLayout()}
      footer={getFooter()}
      className='Sod-ViolationsTabDetailsSidePanel'
      classNames={{
        layoutClassName: 'Sod-ViolationsTabDetailsSidePanel-sidePanelLayout',
        headerClassName: 'Sod-ViolationsTabDetailsSidePanel-sidePanelHeader',
        innerContentClassName: cx(
          'Sod-ViolationsTabDetailsSidePanel-sidePanelInnerContent',
          isMitigatingTab &&
            'Sod-ViolationsTabDetailsMitigateSP--isMitigatingTab'
        ),
        footerClassName: cx(
          'Sod-ViolationsTabDetailsSidePanel-footer',
          !isMitigatingTab &&
            'Sod-ViolationsTabDetailsSidePanel-footer--isConflictTab'
        ),
      }}
    />
  );
}

export default memo(ViolationsTabDetailsSidePanel);
